import React, { useState } from 'react';
import cx from 'classnames';
import { Form, Formik } from 'formik';
import { uniq, without } from 'lodash';

import { InfoButton } from '@magoosh/lib/button';
import Dropdown from '@magoosh/lib/dropdown';

import CheckboxFilter from './checkbox_filter';
import { FilterConfig } from './types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  disabled: boolean;
  filterConfig: FilterConfig;
  onChange: (name: string, value: string) => void;
  value: string;
}

const MultipleSelectFilter: React.FC<Props> = (props) => {
  // Setup options with an 'all' option at the front if asked for
  let options = props.filterConfig.options;
  if (props.filterConfig.all) {
    options = [{ name: 'All', value: 'all' }, ...options];
  }

  const backgroundColor = () => {
    if (props.value !== 'all') {
      if (props.disabled) {
        return colors.grayLighter;
      } else {
        return colors.blue;
      }
    } else {
      if (props.disabled) {
        return colors.grayLighter;
      } else {
        return colors.blueLight;
      }
    }
  };

  const color = () => {
    if (props.value !== 'all') {
      if (props.disabled) {
        return colors.grayLight;
      } else {
        return '#fff';
      }
    } else {
      if (props.disabled) {
        return colors.grayLight;
      } else {
        return colors.blue;
      }
    }
  };

  const handleFilterChanged = (values) => {
    props.onChange(props.filterConfig.key, values[props.filterConfig.key]);
  };

  const valueLabel = () => {
    const values = props.value.split(',');
    const selectedOptions = options.filter((option) => values.indexOf(option.value) !== -1);

    if (selectedOptions.length === 1) {
      return selectedOptions[0].name;
    } else {
      return `Multiple (${selectedOptions.length})`;
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {
        props.disabled ? (
          <div
            className={cx(style.filterButton, 'align-items-center d-flex small')}
            style={{
              backgroundColor: backgroundColor(),
              borderRadius: 4,
              color: color(),
              cursor: props.disabled ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
              gap: 5
            }}
          >
            {props.filterConfig.name}: {valueLabel()}
            <Dropdown.Chevron />
          </div>
        ) : (
          <Dropdown>
          <Dropdown.Trigger style={{ height: '100%' }}>
            {({ isExpanded }) => {
              return (
                <div
                  className={cx(style.filterButton, 'align-items-center d-flex small')}
                  style={{
                    backgroundColor: backgroundColor(),
                    borderRadius: 4,
                    color: color(),
                    cursor: props.disabled ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                    gap: 5
                  }}
                >
                  {props.filterConfig.name}: {valueLabel()}
                  <Dropdown.Chevron isExpanded={isExpanded} />
                </div>
              )
            }}
          </Dropdown.Trigger>

          <Dropdown.Content>
            {({ close }) => {
              return (
                <div className="d-flex flex-column u-padding-A-s" style={{ maxHeight: 535 }}>
                  <Formik
                    enableReinitialize
                    initialValues={{ [props.filterConfig.key]: props.value } as any}
                    onSubmit={(values) => {
                      handleFilterChanged(values);
                      close();
                    }}
                  >
                    {({ values, isSubmitting, setFieldValue }) => (
                      <Form className={cx(style.labelNoWrap, 'd-flex flex-column gap-s')} style={{ minHeight: 0 }}>
                        <div style={{ overflowY: 'auto', flexShrink: 1 }}>
                          <CheckboxFilter
                            filterConfig={props.filterConfig}
                            onChange={(values) => setFieldValue(props.filterConfig.key, values)}
                            value={values[props.filterConfig.key]}
                          />
                        </div>

                        <InfoButton block size="sm" type="submit">
                          Apply
                        </InfoButton>
                      </Form>
                    )}
                  </Formik>
                </div>
              );
            }}
          </Dropdown.Content>
        </Dropdown>
        )
      }
    </div>
  );
};

export default MultipleSelectFilter;
